import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { useLayoutEffect, useRef } from 'react';
import { ChartGroupContainer } from '../containers/chart-group-container';
import { GeneLegend } from '../legends/gene-legend';
import { HeatmapLegend } from '../legends/heatmap-legend';
import { SampleLegend } from '../legends/sample-legend';
import { MultiPlot } from './multi-plot';

const legendWidth = '210px';
export function BarBoxMap() {
  const { queryFilters } = useResearchContext();

  const { setGroupingStable, filters, toggleGeneGroupStable, setAbundanceModeStable, toggleEnvironmentStable } =
    queryFilters;

  const {
    plotData,
    setGraphAndLegendContainerWidth,
    graphAndLegendContainerWidth,
    loading,
    adminFeatures,
    project,
    tooManySamplesToShow,
  } = useResearchContext();

  const barBoxPlotRef = useRef<HTMLDivElement>(null);
  const legendBoxRef = useRef<HTMLDivElement>(null);

  const showOnlyTicks = tooManySamplesToShow;

  useLayoutEffect(() => {
    const handleResize = () => {
      const plotWidth = (barBoxPlotRef.current?.offsetWidth || 0) - (legendBoxRef.current?.offsetWidth || 0);
      setGraphAndLegendContainerWidth(plotWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!graphAndLegendContainerWidth && barBoxPlotRef.current && legendBoxRef.current) {
    const plotWidth = (barBoxPlotRef.current.offsetWidth || 2) - (legendBoxRef.current.offsetWidth || 1);
    setGraphAndLegendContainerWidth(plotWidth);
  }

  if (loading) {
    return null;
  } else if (!plotData) {
    return (
      <Flex justifyContent="center" alignItems="center" height="150px" fontSize="30px" fontWeight="bold">
        No data found for this project
      </Flex>
    );
  }

  return (
    <ChartGroupContainer style={{ paddingBottom: '36px' }} className={TestSelectors.TEST_RESEARCH_CHARTS}>
      <ChartAndLegendContainer>
        <Flex flexDirection="row" ref={barBoxPlotRef}>
          <LegendContainer ref={legendBoxRef}>
            <GeneLegend
              id={TestSelectors.DETECTED_GENES_LEGEND}
              showOptions={false}
              filters={filters}
              setGrouping={setGroupingStable}
              availableGroups={plotData.geneGroups}
              header="Detected genes"
              description="Number of genes by class"
              toggleGeneGroup={toggleGeneGroupStable}
              legendHeight="270px"
              updateSampleStatuses={adminFeatures.updateSampleStatuses}
              project={project}
            />
          </LegendContainer>
          <Box>
            <MultiPlot
              width={graphAndLegendContainerWidth}
              type="bar"
              chartId="research-view-detected-genes-chart"
              showSampleNumbers={true}
              project={project}
              showOnlyTicks={showOnlyTicks}
            />
          </Box>
        </Flex>
      </ChartAndLegendContainer>
      <ChartAndLegendContainer>
        <Flex flexDirection="row">
          <LegendContainer>
            <SampleLegend
              id={TestSelectors.SAMPLES_LEGEND}
              filters={filters}
              toggleEnvironment={toggleEnvironmentStable}
              project={project}
              legendHeight="350px"
              showSampleNumbering={!showOnlyTicks}
            />
          </LegendContainer>
          <Box>
            <MultiPlot
              chartId="research-view-samples-chart"
              width={graphAndLegendContainerWidth}
              type="box"
              showSampleNumbers={true}
              project={project}
              showOnlyTicks={showOnlyTicks}
            />
          </Box>
        </Flex>
      </ChartAndLegendContainer>
      <ChartAndLegendContainer>
        <Flex flexDirection="row">
          <LegendContainer>
            <HeatmapLegend
              id={TestSelectors.HEATMAP_LEGEND}
              abundanceMode={filters.abundances}
              setAbundanceMode={setAbundanceModeStable}
              legendHeight="170px"
            />
          </LegendContainer>
          <Box>
            <MultiPlot
              chartId="research-view-gene-details-chart"
              width={graphAndLegendContainerWidth}
              type="heat"
              showSampleNumbers={true}
              project={project}
              showOnlyTicks={showOnlyTicks}
            />
          </Box>
        </Flex>
      </ChartAndLegendContainer>
    </ChartGroupContainer>
  );
}

const ChartAndLegendContainer = styled(Box)`
  padding: 15px;
  padding-top: 2px;
  padding-bottom: 0;
`;

const LegendContainer = styled.div`
  min-width: ${legendWidth};
  max-width: ${legendWidth};
  height: 270px;
`;
